import { z } from 'zod'
import {
  type ItemPriority,
  type ItemType,
  itemTypeSchema,
  PERSON_NAME_MAX,
  tagSchema,
} from './items'
import { addDays, formatLocalDate, localDateOf } from './time'

/**
 * Priority rules: "#client → high", "from Anna Keller → high", "admin → low", "due within 2 days
 * → high". They decide an item's priority while the user has not set one; the first rule that
 * matches wins. Deterministic (no AI), worked out when hugo plans, so "due within" follows time.
 */

export const PRIORITY_RULES_MAX = 20
export const DUE_WITHIN_DAYS_MAX = 30

export const priorityRuleConditionSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('tag'), tag: tagSchema }),
  z.object({ kind: z.literal('person'), name: z.string().trim().min(1).max(PERSON_NAME_MAX) }),
  z.object({ kind: z.literal('type'), type: itemTypeSchema }),
  z.object({
    kind: z.literal('due_within'),
    days: z.number().int().min(0).max(DUE_WITHIN_DAYS_MAX),
  }),
])
export type PriorityRuleCondition = z.output<typeof priorityRuleConditionSchema>

export const priorityRuleSchema = z.object({
  when: priorityRuleConditionSchema,
  /** A rule raises or lowers; normal is what no rule says. */
  priority: z.enum(['high', 'low']),
})
export type PriorityRule = z.output<typeof priorityRuleSchema>

export const priorityRulesSchema = z.array(priorityRuleSchema).max(PRIORITY_RULES_MAX)

/** Where an item's priority comes from: the user, one of their rules, or neither (normal). */
export type PriorityOrigin = 'you' | 'rule' | 'default'

export type PriorityItem = {
  priority: ItemPriority
  prioritySet: boolean
  tags: readonly string[]
  counterpartName: string | null
  type: ItemType | null
  /** ISO instant. */
  dueAt: string | null
}

function matches(
  when: PriorityRuleCondition,
  item: PriorityItem,
  nowMs: number,
  timeZone: string,
): boolean {
  switch (when.kind) {
    case 'tag':
      return item.tags.includes(when.tag)
    case 'person':
      return item.counterpartName?.trim().toLowerCase() === when.name.toLowerCase()
    case 'type':
      return item.type === when.type
    case 'due_within': {
      if (item.dueAt === null) return false
      // By the user's days: due on or before the day `days` days from today (overdue included).
      const dueOn = formatLocalDate(localDateOf(Date.parse(item.dueAt), timeZone))
      const last = formatLocalDate(addDays(localDateOf(nowMs, timeZone), when.days))
      return dueOn <= last
    }
  }
}

/**
 * An item's priority as hugo plans with it: what the user set; else the first of their rules that
 * matches; else normal. Says where it came from, and which rule.
 */
/** An item's priority, where it comes from, and the rule (and its place in the list) if one set it. */
export type ItemPriorityOf = {
  priority: ItemPriority
  by: PriorityOrigin
  rule: PriorityRule | null
  ruleIndex: number | null
}

export function priorityOf(
  item: PriorityItem,
  rules: { priorityRules: readonly PriorityRule[] },
  nowMs: number,
  timeZone: string,
): ItemPriorityOf {
  if (item.prioritySet) return { priority: item.priority, by: 'you', rule: null, ruleIndex: null }
  const index = rules.priorityRules.findIndex((r) => matches(r.when, item, nowMs, timeZone))
  const rule = rules.priorityRules[index]
  if (!rule) return { priority: 'normal', by: 'default', rule: null, ruleIndex: null }
  return { priority: rule.priority, by: 'rule', rule, ruleIndex: index }
}
